import React from "react";

/**
 * <StorePhoto /> — Renders a real-store photo with WebP source + JPG fallback
 * and proper alt text. Hits /api/static/uploads/store/{slug}.{webp,jpg} via the
 * backend API host.
 *
 * Props:
 *   slug     — "storefront-night" | "entrance-stone-wall" | "wine-aisle" |
 *              "beer-cave" | "liqueur-aisle" | "allocated-weller-bottle"
 *   alt      — required, descriptive alt text
 *   style    — passed through to <img>
 *   className
 *   loading  — "lazy" (default) | "eager"
 *   sizes    — responsive sizes hint (optional)
 *   testid   — data-testid override
 */

const API_HOST = process.env.REACT_APP_BACKEND_URL;

export const STORE_PHOTOS = {
    "storefront-night": {
        alt: "Liquor Studio storefront in Clinton, Missouri at night with illuminated sign",
        caption: "Clinton's beacon — open late, lit up, ready when you are.",
        credit: "309 S 2nd St, Clinton, MO",
    },
    "entrance-stone-wall": {
        alt: "Inside Liquor Studio Clinton MO — stacked-stone entrance with automatic doors and beer displays",
        caption: "Step inside the Studio — stacked stone, neon, and a wall of cold beer.",
        credit: "Front entrance, interior view",
    },
    "wine-aisle": {
        alt: "Liquor Studio Clinton MO wine aisle with hundreds of bottles",
        caption: "Hundreds of wines hand-picked for every price point and palate.",
        credit: "Wine aisle",
    },
    "beer-cave": {
        alt: "Liquor Studio Clinton MO beer cave with cold beer selection",
        caption: "Henry County's deepest cold-beer selection — including the Busch Light hometown loyalty.",
        credit: "Beer cave",
    },
    "liqueur-aisle": {
        alt: "Liquor Studio Clinton MO liqueur and spirits aisle",
        caption: "From Amaretto to Aperol — your home-bar starts here.",
        credit: "Liqueur & spirits aisle",
    },
    "allocated-weller-bottle": {
        alt: "W.L. Weller Special Reserve Kentucky Straight Bourbon at Liquor Studio Clinton MO — an allocated wheated bourbon from the Van Winkle family",
        caption: "Yes, we get the allocated ones. Weller, Blanton's, Eagle Rare, and yes — Van Winkle when it lands. Ask our team about current availability.",
        credit: "W.L. Weller Special Reserve — Buffalo Trace allocated",
    },
};

export const storePhotoUrl = (slug, ext = "jpg") =>
    `${API_HOST}/api/static/uploads/store/${slug}.${ext}`;

export const StorePhoto = ({
    slug,
    alt,
    style = {},
    className = "",
    loading = "lazy",
    sizes = "(max-width: 768px) 100vw, 50vw",
    testid,
    children,
}) => {
    const meta = STORE_PHOTOS[slug];
    const finalAlt = alt || meta?.alt || "Liquor Studio Clinton MO";
    const webp = storePhotoUrl(slug, "webp");
    const jpg = storePhotoUrl(slug, "jpg");
    return (
        <picture data-testid={testid || `store-photo-${slug}`}>
            <source srcSet={webp} type="image/webp" sizes={sizes} />
            <img
                src={jpg}
                alt={finalAlt}
                loading={loading}
                decoding="async"
                style={{ display: "block", width: "100%", height: "100%", objectFit: "cover", ...style }}
                className={className}
            />
            {children}
        </picture>
    );
};

export default StorePhoto;
